import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Layout, PageHeader, Avatar, Drawer } from 'antd';
import { CloseOutlined, InfoCircleFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, message, Modal, Tooltip } from 'antd-v5';
import { useQueryClient } from 'react-query';
import ConversationWindow from '../../../Layout/chat/chat';
import { updateClient } from '../../../../services/clients';
import { postRequest } from '../../../../core/apiRequests';
import { setClientAssociateId } from '../../../../reducers/client';
import { setMsgReminderId, setMsgReminderDescription, setMsgClient } from '../../../../reducers/messaging';
import getAvatarColor from '../../../../utils/getAvatarColor';

const Header = props => {
  const [loading, setLoading] = useState(false);

  const initials = props.client?.name
    ?.split(' ')
    .map(e => e.charAt(0))
    .slice(0, 2)
    .join('');

  const avatarColor = getAvatarColor(props.client?.name);

  const nameShorten = name => {
    if (name) {
      if (window.innerWidth < 1515 && name.length > 30) {
        return `${name.substr(0, 30)}...`;
      }
      if (window.innerWidth < 1642 && name.length > 58) {
        return `${name.substr(0, 58)}...`;
      }
      if (name.length > 80) {
        return `${name.substr(0, 80)}...`;
      }
    }
    return name || '';
  };

  const queryClient = useQueryClient();
  const associates_names = queryClient.getQueryData('associates-list-info-drawer');
  const associateName = useMemo(() => {
    if (!props.client.associate_id) {
      return 'Unassigned';
    }
    if (props.client.associate_id !== props.auth.userId) {
      const associate = associates_names?.find(asct => asct.value === props.client.associate_id);
      return `Assigned to ${associate?.label}`;
    }

    return '';
  }, [props.client.associate_id, props.auth.userId, associates_names]);

  const assignToMe = useCallback(
    async associateId => {
      const previousAssociateId = props.client.associate_id;
      const associateChanged = previousAssociateId && previousAssociateId !== associateId;

      // Show confirmation modal if there's a previous associate being replaced
      if (associateChanged) {
        const isUnassigning = associateId === 0;
        const confirmMessage = isUnassigning
          ? 'Unassigning this client will also unassign any open reminders currently assigned to the previous associate. Open sales opportunities will remain with the previous associate. Do you want to continue?'
          : 'Changing the assigned associate will also reassign any open reminders and open sales opportunities currently assigned to the previous associate. Do you want to continue?';

        const confirmed = await new Promise(resolve => {
          Modal.confirm({
            title: isUnassigning ? 'Unassign Associate' : 'Reassign Associate',
            content: confirmMessage,
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });

        if (!confirmed) return;
      }

      try {
        setLoading(true);
        await updateClient({
          data: {
            associateId,
          },
          clientId: props.client.client_id,
        });

        // Cascade reassignment of reminders and open opportunities (best-effort)
        if (associateChanged) {
          try {
            await postRequest({
              params: ['clients', props.client.client_id, 'reassignAssociateItems'],
              data: {
                previousAssociateId,
                newAssociateId: associateId || 0,
              },
            });
          } catch (cascadeErr) {
            console.error('Cascade reassignment failed:', cascadeErr);
          }
        }

        setClientAssociateId(associateId === 0 ? null : associateId);
      } catch (error) {
        message.error('Failed to assign client to you');
      } finally {
        setLoading(false);
      }
    },
    [updateClient, props.client.client_id, props.auth.userId, props.client.associate_id],
  );

  const assignButton = useCallback(() => {
    if (!props.client.associate_id) {
      return (
        <Button type="link" className="cb-link-btn-action" onClick={() => assignToMe(props.auth.userId)} loading={loading}>
          <FontAwesomeIcon icon={['far', 'bookmark']} />
          Assign To Me
        </Button>
      );
    }
    if (props.client.associate_id === props.auth.userId) {
      return (
        <Button type="link" className="cb-link-btn-action" onClick={() => assignToMe(0)} loading={loading}>
          <FontAwesomeIcon icon={['fas', 'bookmark']} />
          Unassign
        </Button>
      );
    }
    if (props.auth.role === 'ASSOCIATE') {
      return (
        <Tooltip
          title={`Ask a manager to assign you. This client is already assigned to ${
            props?.client?.associate
              ? `${props?.client?.associate.first_name || ''} ${props?.client?.associate?.last_name || ''}`
              : 'an Associate'
          }. You can only assign yourself to unassigned clients.`}
          placement="bottom"
        >
          <Button type="link" className="cb-link-btn-action" loading={loading} disabled>
            <FontAwesomeIcon icon={['far', 'bookmark']} />
            Assign To Me
          </Button>
        </Tooltip>
      );
    }
    if (props.client.associate_id !== props.auth.userId) {
      return (
        <Button type="link" className="cb-link-btn-action" onClick={() => assignToMe(props.auth.userId)} loading={loading}>
          <FontAwesomeIcon icon={['far', 'bookmark']} />
          Assign To Me
        </Button>
      );
    }
    return null;
  }, [props.client, props.auth, loading]);

  return (
    <div style={{ marginTop: '-5px' }}>
      <Layout className="conversation-window-layout" style={{ maxHeight: '100%', background: '#FFFFFF' }}>
        <div className="conversation-window-header flex-row-spacebetween-nowrap">
          <div className="add-edit-wrap">
            <Avatar
              className="conversation-window-header-user-icon"
              id="user-icon"
              size="medium"
              key={initials}
              style={{
                background: avatarColor.bg,
                color: '#FFF',
                width: '52px',
                height: '52px',
                marginTop: '14px',
                lineHeight: '52px',
                fontSize: '18px',
                fontWeight: '600',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
              }}
            >
              {initials}
            </Avatar>
            <PageHeader
              className="conversation-window-header-heading-sub"
              id="middle-panel-header"
              title={nameShorten(props.client?.name)}
              subTitle={`${props.client?.preferred_name || ''} ${props.client?.preferred_name ? '|' : ''} ${
                props.client?.preferred_contact_type
              } ${associateName}`}
            />
          </div>
          {/* <StoreModal auth={props.auth} hideSelector={props.type === 'full'} /> */}
          <div className="headingIcons" id="heading-icons">
            {/* eslint-disable-next-line no-nested-ternary */}
            {props.type === 'right' ? (
              <CloseOutlined
                title="Close"
                style={{ fontSize: '16px' }}
                id="more-info-icon"
                onClick={() => props.setDrawerToggle(true)}
              />
            ) : (
              <>
                {assignButton()}
                <FontAwesomeIcon
                  className="m-x-12 fs-19 gray pointer"
                  color="#818181"
                  icon={['far', 'comment-alt-lines']}
                  onClick={() => {
                    setMsgReminderId(null);
                    setMsgReminderDescription('');
                    // Clear config IDs so templates aren't filtered
                    setMsgClient({
                      tag_action_config_id: null,
                      life_event_action_config_id: null,
                      sales_opp_action_config_id: null,
                      custom_event_action_config_id: null,
                    });
                    props.showMessageHandler();
                  }}
                />
                <Tooltip title="Client Info" placement="bottom">
                  <InfoCircleFilled
                    className="fs-16 pointer"
                    id="more-info-icon"
                    title="Client info"
                    onClick={() => props.setDrawerToggle(!props.toggleDrawer)}
                  />
                </Tooltip>
              </>
            )}
          </div>
        </div>
        <Drawer
          bodyStyle={{
            padding: 0,
            overflow: 'hidden',
          }}
          closable={false}
          push={false}
          destroyOnClose
          width={750}
          open={props.showPopConfirm}
          onClose={props.closeChat}
        >
          <ConversationWindow setDrawerToggle={props.closeChat} chatIconColor="#EBEBEB" type="right" />
        </Drawer>
      </Layout>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  client: state.client,
});
export default connect(mapStateToProps)(Header);
